import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { cleanMarkdownText } from "@/lib/markdown-utils";
import { documentService } from "@/integrations/documents/documentService";
import type { ActiveTab, DocumentFormat } from "@/components/records/types/recordHeader.types";
import type { RecordDetails } from "@/hooks/useRecordDetails";

export const useRecordActions = (record: RecordDetails | null, activeTab: ActiveTab) => {
  const { toast } = useToast();
  const [generatingDoc, setGeneratingDoc] = useState(false);

  const getCurrentText = () => {
    if (!record) return "";
    
    return activeTab === "transcript" 
      ? cleanMarkdownText(record.transcriptText)
      : cleanMarkdownText(record.postProcessingText);
  };

  const copyToClipboard = async () => {
    try {
      const textToCopy = getCurrentText();
      await navigator.clipboard.writeText(textToCopy);

      toast({
        title: "Скопировано!",
        description: "Текст скопирован в буфер обмена",
      });
    } catch (error) {
      console.error("Ошибка при копировании:", error);
      toast({
        title: "Ошибка",
        description: "Не удалось скопировать текст",
        variant: "destructive",
      });
    }
  };

  const downloadText = () => {
    try {
      if (!record) return;
      
      const textToDownload = getCurrentText();
      const fileName = activeTab === "transcript" 
        ? `${record.name}_транскрипция.txt` 
        : `${record.name}_обработанный.txt`;

      const blob = new Blob([textToDownload], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Скачивание",
        description: "Файл успешно скачан",
      });
    } catch (error) {
      console.error("Ошибка при скачивании:", error);
      toast({
        title: "Ошибка",
        description: "Не удалось скачать файл",
        variant: "destructive",
      });
    }
  };

  const getFileName = () => {
    if (!record) return "document";
    
    const baseName = activeTab === "transcript" 
      ? `${record.name}_транскрипция` 
      : `${record.name}_обработанный`;
    
    return baseName;
  };

  const handleDocumentDownload = async (format: DocumentFormat) => {
    if (!record) {
      toast({
        title: "Ошибка",
        description: "Запись не найдена",
        variant: "destructive",
      });
      return;
    }

    try {
      setGeneratingDoc(true);
      
      const text = getCurrentText();
      const filename = getFileName();
      
      toast({
        title: "Генерация документа",
        description: "Создаем документ, пожалуйста подождите...",
      });

      const response = await documentService.generateDocument({
        text,
        format,
        filename,
      });

      if (response.success && response.download_url) {
        await documentService.downloadFile(response.download_url, `${filename}.${format}`);
        
        toast({
          title: "Успешно",
          description: "Документ успешно скачан",
        });
      } else {
        throw new Error(response.message || "Не удалось создать документ");
      }
    } catch (error) {
      console.error("Ошибка при создании документа:", error);
      toast({
        title: "Ошибка",
        description: error instanceof Error ? error.message : "Не удалось создать документ",
        variant: "destructive",
      });
    } finally {
      setGeneratingDoc(false);
    }
  };

  return {
    copyToClipboard,
    downloadText,
    getCurrentText,
    getFileName,
    handleDocumentDownload,
    generatingDoc,
  };
};